import React, { useState } from 'react';
import { DetectReceiptFraudResponse, BaseReceiptFraudDetectionItem } from '../types';

interface DetectReceiptFraudResultsProps {
  results: DetectReceiptFraudResponse;
  onDownload: (url: string) => void;
}

export const DetectReceiptFraudResults: React.FC<DetectReceiptFraudResultsProps> = ({ 
  results, 
  onDownload 
}) => {
  const { result, responses, download_url } = results;

  // State for collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    handwriting: true,
    digitalManipulation: true,
    screenRecapture: true,
    summary: false,
    detailedResponses: false
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Calculate total tokens from all responses
  const totalTokens = responses.reduce((acc, response) => {
    // Add tokens from main response
    let inputTokens = response.token_count.input_token;
    let outputTokens = response.token_count.output_token;
    
    // Add tokens from tool result metadata in messages
    if (response.messages) {
      response.messages.forEach(message => {
        if (message.tool_result_metadata?.token_count) {
          inputTokens += message.tool_result_metadata.token_count.input_token || 0;
          outputTokens += message.tool_result_metadata.token_count.output_token || 0;
        }
      });
    }
    
    return {
      input: acc.input + inputTokens,
      output: acc.output + outputTokens
    };
  }, { input: 0, output: 0 });

  // Check if any fraud signs were detected
  const totalFraudSigns = 
    result.handwriting.length + 
    result.digital_manipulation.length + 
    result.screen_recapture.length;
  
  const hasFraud = totalFraudSigns > 0;

  // Format sign names for display
  const formatSignName = (sign: string): string => {
    return sign.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Render fraud detection items
  const renderFraudItems = (items: BaseReceiptFraudDetectionItem[], category: string) => {
    if (items.length === 0) {
      return (
        <div className="no-fraud-detected">
          <p>✓ No {category} fraud signs detected</p>
        </div>
      );
    }

    return (
      <div className="fraud-items-list">
        {items.map((item, index) => (
          <div key={index} className="fraud-item-card">
            <div className="fraud-item-header">
              <span className="fraud-sign-badge">⚠️ {formatSignName(item.sign)}</span>
            </div>
            {item.affected_data_field && (
              <div className="fraud-item-field">
                <strong>Affected Field:</strong> {item.affected_data_field}
              </div>
            )}
            {item.details && (
              <div className="fraud-item-details">
                <strong>Details:</strong> {item.details}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="results">
      <div className="results-header">
        <h3>Fraud Detection Results</h3>
        <div className="results-header-actions">
          <div className={`fraud-status ${hasFraud ? 'fraud-detected' : 'no-fraud'}`}>
            {hasFraud ? `⚠️ ${totalFraudSigns} Fraud Sign${totalFraudSigns > 1 ? 's' : ''} Detected` : '✓ No Fraud Detected'}
          </div>
          <button
            className="btn btn-success"
            onClick={() => onDownload(download_url)}
          >
            Download Analysis Report
          </button>
        </div>
      </div>

      {/* Handwriting Fraud Section */}
      <div className="results-section">
        <div 
          className="config-header" 
          onClick={() => toggleSection('handwriting')}
        >
          <button 
            type="button" 
            className={`collapse-toggle ${!expandedSections.handwriting ? 'collapsed' : ''}`}
            aria-label={expandedSections.handwriting ? 'Collapse handwriting section' : 'Expand handwriting section'}
          >
            <span>▼</span>
          </button>
          <h4>Handwriting Manipulation ({result.handwriting.length})</h4>
        </div>
        {expandedSections.handwriting && (
          <div className="section-content">
            {renderFraudItems(result.handwriting, 'handwriting')}
          </div>
        )}
      </div>

      {/* Digital Manipulation Section */}
      <div className="results-section">
        <div 
          className="config-header" 
          onClick={() => toggleSection('digitalManipulation')}
        >
          <button 
            type="button" 
            className={`collapse-toggle ${!expandedSections.digitalManipulation ? 'collapsed' : ''}`}
            aria-label={expandedSections.digitalManipulation ? 'Collapse digital manipulation section' : 'Expand digital manipulation section'}
          >
            <span>▼</span>
          </button>
          <h4>Digital Manipulation ({result.digital_manipulation.length})</h4>
        </div>
        {expandedSections.digitalManipulation && (
          <div className="section-content">
            {renderFraudItems(result.digital_manipulation, 'digital manipulation')}
          </div>
        )}
      </div>

      {/* Screen Recapture Section */}
      <div className="results-section">
        <div 
          className="config-header" 
          onClick={() => toggleSection('screenRecapture')}
        >
          <button 
            type="button" 
            className={`collapse-toggle ${!expandedSections.screenRecapture ? 'collapsed' : ''}`}
            aria-label={expandedSections.screenRecapture ? 'Collapse screen recapture section' : 'Expand screen recapture section'}
          >
            <span>▼</span>
          </button>
          <h4>Screen Recapture ({result.screen_recapture.length})</h4>
        </div>
        {expandedSections.screenRecapture && (
          <div className="section-content">
            {renderFraudItems(result.screen_recapture, 'screen recapture')}
          </div>
        )}
      </div>

      {/* Summary Section */}
      <div className="results-section">
        <div 
          className="config-header" 
          onClick={() => toggleSection('summary')}
        >
          <button 
            type="button" 
            className={`collapse-toggle ${!expandedSections.summary ? 'collapsed' : ''}`}
            aria-label={expandedSections.summary ? 'Collapse summary section' : 'Expand summary section'}
          >
            <span>▼</span>
          </button>
          <h4>Token Usage Summary</h4>
        </div>
        {expandedSections.summary && (
          <div className="section-content">
            <div className="token-usage">
              <div className="token-item">
                <span className="token-label">Total Input Tokens:</span>
                <span className="token-value">{totalTokens.input.toLocaleString()}</span>
              </div>
              <div className="token-item">
                <span className="token-label">Total Output Tokens:</span>
                <span className="token-value">{totalTokens.output.toLocaleString()}</span>
              </div>
              <div className="token-item">
                <span className="token-label">Total Tokens:</span>
                <span className="token-value">{(totalTokens.input + totalTokens.output).toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detailed Responses Section */}
      <div className="results-section">
        <div 
          className="config-header" 
          onClick={() => toggleSection('detailedResponses')}
        >
          <button 
            type="button" 
            className={`collapse-toggle ${!expandedSections.detailedResponses ? 'collapsed' : ''}`}
            aria-label={expandedSections.detailedResponses ? 'Collapse detailed responses section' : 'Expand detailed responses section'}
          >
            <span>▼</span>
          </button>
          <h4>Detailed LLM Responses ({responses.length})</h4>
        </div>
        {expandedSections.detailedResponses && (
          <div className="section-content">
            <div className="response-group">
              {responses.map((response, index) => (
                <div key={index} className="response-card">
                  <div className="response-header">
                    <strong>Response {index + 1}</strong>
                    <div>
                      Model: {response.model_info.model_name} ({response.model_info.provider})
                    </div>
                    <div>
                      Tokens: {response.token_count.input_token.toLocaleString()} in / {response.token_count.output_token.toLocaleString()} out
                    </div>
                    <div>
                      Processing Time: {response.processing_time.toFixed(2)}s
                    </div>
                  </div>
                  <div className="response-details">
                    <details open>
                      <summary>View Raw Response</summary>
                      <pre className="raw-text">{(() => {
                        // Priority: raw_value > value > tool_calls
                        if (response.raw_value) {
                          return response.raw_value;
                        }
                        if (response.value) {
                          return typeof response.value === 'string' 
                            ? response.value 
                            : JSON.stringify(response.value, null, 2);
                        }
                        // If no value but has tool calls, show tool calls
                        if (response.tool_calls && response.tool_calls.length > 0) {
                          return JSON.stringify(response.tool_calls, null, 2);
                        }
                        return '(No response content)';
                      })()}</pre>
                    </details>
                    {response.tool_calls && response.tool_calls.length > 0 && (
                      <details>
                        <summary>View Tool Calls ({response.tool_calls.length})</summary>
                        <div className="tool-calls-list">
                          {response.tool_calls.map((toolCall, idx) => (
                            <div key={idx} className="tool-call-card">
                              <div className="tool-call-header">
                                <span className="tool-call-type">{toolCall.type}</span>
                                {toolCall.name && <span className="tool-call-name">{toolCall.name}</span>}
                              </div>
                              {toolCall.id && (
                                <div className="tool-call-id">
                                  <strong>ID:</strong> {toolCall.id}
                                </div>
                              )}
                              {toolCall.args && Object.keys(toolCall.args).length > 0 && (
                                <div className="tool-call-section">
                                  <div className="tool-call-section-title">Arguments:</div>
                                  <div className="tool-call-args">
                                    {Object.entries(toolCall.args).map(([key, value]) => (
                                      <div key={key} className="tool-call-arg-item">
                                        <span className="arg-key">{key}:</span>
                                        <span className="arg-value">
                                          {typeof value === 'object' 
                                            ? JSON.stringify(value, null, 2) 
                                            : String(value)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {toolCall.extra_content && Object.keys(toolCall.extra_content).length > 0 && (
                                <div className="tool-call-section">
                                  <div className="tool-call-section-title">Extra Content:</div>
                                  <pre className="raw-text">{JSON.stringify(toolCall.extra_content, null, 2)}</pre>
                                </div>
                              )}
                              <div className="tool-call-section">
                                <details>
                                  <summary>View Full JSON</summary>
                                  <pre className="raw-text">{JSON.stringify(toolCall, null, 2)}</pre>
                                </details>
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                    {response.prompt && (
                      <details>
                        <summary>View Prompt</summary>
                        <pre className="raw-text">{response.prompt}</pre>
                      </details>
                    )}
                    {response.messages && response.messages.length > 0 && (
                      <details>
                        <summary>View Messages ({response.messages.length})</summary>
                        <div className="messages-list">
                          {response.messages.map((message, msgIdx) => (
                            <div key={msgIdx} className="message-card">
                              <div className="message-header">
                                <strong>Message {msgIdx + 1}</strong>
                                {message.is_tool_result && <span className="message-tag tool-result-tag">Tool Result</span>}
                              </div>
                              {message.tool_result_metadata && (
                                <div className="tool-result-metadata">
                                  <div className="metadata-label">Tool Result Metadata:</div>
                                  {message.tool_result_metadata.token_count && (
                                    <div className="metadata-tokens">
                                      <span className="metadata-item">
                                        Input Tokens: {message.tool_result_metadata.token_count.input_token.toLocaleString()}
                                      </span>
                                      <span className="metadata-item">
                                        Output Tokens: {message.tool_result_metadata.token_count.output_token.toLocaleString()}
                                      </span>
                                    </div>
                                  )}
                                  {message.tool_result_metadata.raw_value && (
                                    <details>
                                      <summary>View Raw Value</summary>
                                      <pre className="raw-text">{message.tool_result_metadata.raw_value}</pre>
                                    </details>
                                  )}
                                </div>
                              )}
                              <details>
                                <summary>View Full Message JSON</summary>
                                <pre className="raw-text">{JSON.stringify(message, null, 2)}</pre>
                              </details>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
